/* eslint-disable no-param-reassign */
import type {} from '@redux-devtools/extension'; // required for devtools typing
import { type ChainflipChain, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { type ChainflipAccountId, type InjectedAccountWithMeta } from '@/shared/hooks/usePolkadot';
import type { DepositChannel } from '@/shared/hooks/useStateChainAccount';

export type StoredDepositChannel = Omit<DepositChannel, 'asset'> & {
  asset: ChainflipAsset;
};

const DEFAULT_ADDRESS_MAP = {
  Bitcoin: null,
  Ethereum: null,
  Arbitrum: null,
  Assethub: null,
  Solana: null,
  Tron: null,
  Bsc: null,
} as const;

interface Store {
  addChannel: (channel: DepositChannel) => void;
  removeChannel: (depositAddress: string) => void;
  channels: StoredDepositChannel[];
  polkadotAccount: InjectedAccountWithMeta | null;
  savePolkadotAccount: (account: InjectedAccountWithMeta | null) => void;
  /** a temporary store for refund addresses for users onboarding via deposit channel */
  refundAddresses: Record<ChainflipChain, string | null>;
  setRefundAddress: (chain: ChainflipChain, address: string) => void;
}

const useStore = create<Store>()(
  devtools(
    persist(
      immer((set) => ({
        polkadotAccount: null,
        savePolkadotAccount: (account) =>
          set(
            (state) => {
              state.polkadotAccount = account;
              state.refundAddresses = DEFAULT_ADDRESS_MAP;
            },
            false,
            'savePolkadotAccount',
          ),
        channels: [],
        addChannel: ({ asset, ...channel }) =>
          set(
            (state) => {
              state.channels.push({
                ...channel,
                asset: asset.chainflipId,
              });
            },
            false,
            'addChannel',
          ),
        removeChannel: (depositAddress) =>
          set(
            (state) => {
              state.channels = state.channels.filter(
                (channel) => channel.depositAddress !== depositAddress,
              );
            },
            false,
            'removeChannel',
          ),
        refundAddresses: DEFAULT_ADDRESS_MAP,
        setRefundAddress: (chain, address) =>
          set(
            (state) => {
              state.refundAddresses[chain] = address;
            },
            false,
            'setRefundAddress',
          ),
      })),
      {
        name: 'cf.liquidity-provision',
        version: 5,
        migrate: (persistedState, version) => {
          if (version === 1 && typeof persistedState === 'object') {
            // remove channels stored with previous shape as they are temporary anyway
            persistedState = { ...(persistedState as Store), channels: [] };
          }

          if (version === 2 && typeof persistedState === 'object') {
            const typedState = persistedState as Store & {
              polkadotAccount?: { id: ChainflipAccountId };
            };

            // migrate renamed id property on account object
            if (typedState.polkadotAccount) {
              typedState.polkadotAccount = {
                ...typedState.polkadotAccount,
                idSs58: typedState.polkadotAccount.id,
              };
            }
          }

          if (version === 3 && typeof persistedState === 'object') {
            const typedState = persistedState as Store;
            if (typedState.polkadotAccount) {
              typedState.polkadotAccount = { ...typedState.polkadotAccount, type: 'substrate' };
            }
          }

          if (version < 5 && typeof persistedState === 'object') {
            const typedState = persistedState as Store;
            // reset refund addresses to new default
            typedState.refundAddresses = DEFAULT_ADDRESS_MAP;
          }

          return persistedState as Store;
        },
      },
    ),
  ),
);

export default useStore;
